// "Your Namey": the player's own mascot. Pick a base, a colour and a resting
// face. Saved to the account so web and mobile match; only the player sees it.
import React, { useState } from "react";
import { ChevronRight, Smile } from "lucide-react";
import BottomSheet from "../BottomSheet";
import SettingsRow from "./SettingsRow";
import {
  MascotAvatar,
  MASCOT_BASES,
  MASCOT_COLORS,
  PALETTES,
  PERSONALITY_FACES,
  saveMyMascot,
  useMyMascot,
} from "../Mascot";

const COLOR_NAMES = {
  red: "Red", orange: "Orange", amber: "Sunshine", teal: "Green",
  blue: "Blue", purple: "Purple", berry: "Berry",
};

const Row = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-bold text-brand-textSecondary uppercase tracking-wide mb-2">{label}</p>
    {children}
  </div>
);

const MascotEditor = () => {
  const mascot = useMyMascot();
  const [bounce, setBounce] = useState(0);
  const update = (patch) => {
    saveMyMascot({ ...mascot, ...patch });
    setBounce((n) => n + 1);
  };

  return (
    <div className="px-4 py-4 space-y-4">
      <button
        type="button"
        onClick={() => setBounce((n) => n + 1)}
        aria-label="Your Namey preview"
        className="mx-auto flex h-32 w-32 items-end justify-center rounded-2xl bg-brand-accentMuted"
      >
        <MascotAvatar
          key={bounce}
          mascot={mascot}
          mood="calm"
          size={mascot.base === "pencil" ? 58 : 88}
          className={bounce ? "nm-pop" : ""}
        />
      </button>

      <Row label="Base">
        <div role="radiogroup" aria-label="Base" className="grid grid-cols-2 gap-2">
          {MASCOT_BASES.map((b) => {
            const selected = mascot.base === b.id;
            return (
              <button
                key={b.id}
                role="radio"
                aria-checked={selected}
                onClick={() => update({ base: b.id })}
                className={`flex items-center justify-center gap-2 rounded-lg border-2 py-2 text-sm font-semibold transition-colors ${
                  selected ? "border-brand-accent bg-brand-accentMuted text-brand-accent" : "border-brand-border text-brand-text hover:bg-brand-surface2"
                }`}
              >
                <span className="flex h-9 items-end">
                  <MascotAvatar mascot={{ ...mascot, base: b.id }} size={b.id === "pencil" ? 24 : 30} shadow={false} />
                </span>
                {b.name}
              </button>
            );
          })}
        </div>
      </Row>

      <Row label="Colour">
        <div role="radiogroup" aria-label="Colour" className="grid grid-cols-7 gap-2">
          {MASCOT_COLORS.map((c) => {
            const selected = mascot.color === c;
            return (
              <button
                key={c}
                role="radio"
                aria-checked={selected}
                aria-label={COLOR_NAMES[c]}
                title={COLOR_NAMES[c]}
                onClick={() => update({ color: c })}
                className={`aspect-square w-full max-w-9 rounded-full transition-transform ${selected ? "ring-2 ring-offset-2 ring-brand-accent scale-110" : "hover:scale-105"}`}
                style={{ background: `linear-gradient(${PALETTES[c].top}, ${PALETTES[c].bot})` }}
              />
            );
          })}
        </div>
      </Row>

      <Row label="Face">
        <div role="radiogroup" aria-label="Face" className="grid grid-cols-3 gap-2">
          {Object.entries(PERSONALITY_FACES).map(([id, f]) => {
            const selected = mascot.face === id;
            return (
              <button
                key={id}
                role="radio"
                aria-checked={selected}
                onClick={() => update({ face: id })}
                className={`flex flex-col items-center rounded-lg border-2 pt-2 pb-1.5 transition-colors ${
                  selected ? "border-brand-accent bg-brand-accentMuted" : "border-brand-border hover:bg-brand-surface2"
                }`}
              >
                <span className="flex h-12 items-end">
                  <MascotAvatar mascot={{ ...mascot, face: id }} size={mascot.base === "pencil" ? 32 : 40} shadow={false} />
                </span>
                <span className={`text-xs font-semibold mt-1 ${selected ? "text-brand-accent" : "text-brand-text"}`}>{f.label}</span>
              </button>
            );
          })}
        </div>
      </Row>

      <p className="text-xs text-brand-textSecondary">
        Your Namey peeks over your card during rounds and lives on the home page. Only you see it, on web and mobile.
      </p>
    </div>
  );
};

export const MascotSettingsRow = () => {
  const mascot = useMyMascot();
  const [open, setOpen] = useState(false);
  return (
    <>
      <SettingsRow
        title="Namey, your mascot"
        subtitle="How you look in games"
        icon={Smile}
        onClick={() => setOpen(true)}
        rightElement={
          <span className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-end justify-center">
              <MascotAvatar mascot={mascot} size={mascot.base === "pencil" ? 24 : 34} shadow={false} />
            </span>
            <ChevronRight size={16} className="text-brand-placeholder" />
          </span>
        }
      />
      <BottomSheet isOpen={open} onClose={() => setOpen(false)} title="Your Namey" subtitle="Pick a base, colour and face">
        <MascotEditor />
      </BottomSheet>
    </>
  );
};

export default MascotEditor;
